'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import { Badge } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridContainerVariants,
  gridItemVariants,
  reducedMotionVariants,
} from '../../lib/motion'
import { CodeWindowBase } from '../code-window'

/** Single step in the how-it-works flow */
export interface HowItWorksLongStep {
  /** Step number displayed as badge (e.g., "01", "02") */
  number: string
  title: string
  /** Step description - supports Markdown (inline code, links, bold, etc.) */
  description: string
  code: { code: string; language: string }
}

export interface HowItWorksLongProps {
  /** Badge text */
  badge?: string
  /** Section heading */
  heading?: string
  /** Section description */
  description?: string
  /** Array of steps (typically 3-4) */
  steps?: HowItWorksLongStep[]
  className?: string
}

const defaultSteps: HowItWorksLongStep[] = [
  {
    number: '01',
    title: 'Install the package',
    description:
      'Add MDXUI to your project with a single command. Works with `npm`, `yarn`, `pnpm`, or `bun`.',
    code: {
      code: `npm install mdxui`,
      language: 'bash',
    },
  },
  {
    number: '02',
    title: 'Import components',
    description:
      'Import the components you need from the library. Full TypeScript support included.',
    code: {
      code: `import { Hero, Features, CTA } from 'mdxui'
import type { HeroProps } from 'mdxui'

// All components are fully typed
const heroProps: HeroProps = {
  title: "Welcome",
  description: "Build faster",
}`,
      language: 'typescript',
    },
  },
  {
    number: '03',
    title: 'Build your page',
    description:
      'Compose components together to create beautiful interfaces. Customize as needed.',
    code: {
      code: `export default function LandingPage() {
  return (
    <>
      <Hero
        title="Welcome to MDXUI"
        description="Type-safe components"
        primaryButton={{ text: 'Get Started', url: '/docs' }}
      />
      <Features features={featureList} />
      <CTA heading="Ready to build?" />
    </>
  )
}`,
      language: 'typescript',
    },
  },
]

/**
 * HowItWorksLong - Stacked steps with code on right
 *
 * **Use this variant when:**
 * - Code snippets are 5+ lines
 * - You need to show meaningful code context
 * - Users shouldn't have to scroll within code windows
 *
 * **Layout:**
 * ```
 * ┌────────────────────────────────────────────────┐
 * │ ┌─────────────────┬──────────────────────────┐ │
 * │ │ [01]            │                          │ │
 * │ │ Title           │ Code snippet (5-15 lines)│ │
 * │ │ Description     │ with syntax highlighting │ │
 * │ └─────────────────┴──────────────────────────┘ │
 * ├────────────────────────────────────────────────┤
 * │ ┌─────────────────┬──────────────────────────┐ │
 * │ │ [02]            │                          │ │
 * │ │ Title           │ Code snippet             │ │
 * │ │ Description     │                          │ │
 * │ └─────────────────┴──────────────────────────┘ │
 * └────────────────────────────────────────────────┘
 * ```
 *
 * For short 1-3 line snippets, use `HowItWorksCards` (3-column grid) instead.
 */
const HowItWorksLong = ({
  badge = 'How it works',
  heading = 'Empower your business with our solutions',
  description = 'Discover how our tools can transform your workflow and help you achieve your goals faster.',
  steps = defaultSteps,
  className,
}: HowItWorksLongProps) => {
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const headerAnimation = prefersReducedMotion ? reducedMotionVariants : headerVariants
  const itemAnimation = prefersReducedMotion ? reducedMotionVariants : gridItemVariants

  return (
    <motion.section
      ref={sectionRef}
      className={cn('bg-muted py-16 sm:py-24 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="flex flex-col items-center pb-8 text-center"
          variants={headerAnimation}
        >
          <span className="mb-6 flex items-center justify-center">
            <Badge variant="outline" className="bg-background px-3 py-1">
              {badge}
            </Badge>
          </span>
          <h2 className="pb-4 text-3xl font-semibold sm:text-4xl text-balance">
            {heading}
          </h2>
          <div className="max-w-md lg:max-w-2xl text-balance">
            <Markdown size="base">{description}</Markdown>
          </div>
        </motion.div>

        {/* Stacked Steps */}
        <motion.div
          className="mx-auto mt-8 flex max-w-5xl flex-col gap-6"
          variants={gridContainerVariants(0.1, 0.2)}
        >
          {steps.map((step) => (
            <motion.div
              key={step.number}
              className="overflow-hidden rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-background shadow-sm"
              variants={itemAnimation}
            >
              {/* Two-column layout: [number + title + description] | [code] */}
              <div className="grid grid-cols-1 lg:grid-cols-2">
                {/* Left side: number, title, description */}
                <div className="flex flex-col justify-center border-b border-border p-6 lg:border-b-0 lg:border-r">
                  <Badge variant="outline" className="mb-4 w-fit rounded-[var(--radius-sm,calc(var(--radius)*0.5))] bg-muted py-2 font-mono">
                    {step.number}
                  </Badge>
                  <h3 className="mb-3 text-lg font-semibold">{step.title}</h3>
                  <div className="prose prose-sm max-w-none text-muted-foreground prose-strong:text-foreground prose-code:rounded-[var(--radius-sm,calc(var(--radius)*0.5))] prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:font-normal prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
                    <ReactMarkdown>{step.description}</ReactMarkdown>
                  </div>
                </div>

                {/* Right side: code window */}
                <div className="flex min-h-[200px] flex-col">
                  <CodeWindowBase
                    code={step.code.code}
                    language={step.code.language}
                    showHeader={false}
                    showCopy={false}
                    theme="dark"
                    style="borderless"
                    className="h-full min-h-0 flex-1"
                    contentClassName="h-full"
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  )
}

export { HowItWorksLong }
